import React, { Fragment } from 'react';
import { TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { articles, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, tierLabels, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-697">
      <div className="text-sm text-muted-foreground" data-id="element-698">{label}</div>
      <div className={`text-3xl md:text-4xl font-semibold leading-none ${accent ? 'text-accent' : 'text-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-699">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm" data-id="element-700">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-701" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-702" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-703">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-704">vs prec.</span>
        </div>}
    </div>;
}
export function Dashboard() {
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <div className="p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-705">
      {/* Page header */}
      <header data-id="element-706">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-id="element-707">
          Dashboard
        </h1>
      </header>

      {/* KPI strip */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-708">
        <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-709" />
        <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-710" />
        <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-711" />
        <div className="flex flex-col gap-2" data-id="element-712">
          <div className="text-sm text-muted-foreground" data-id="element-713">Sentiment</div>
          <div className="flex items-baseline gap-2 leading-none" data-id="element-714">
            <span className="text-2xl md:text-3xl font-semibold text-sentiment-positive" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-715">
              {dashboardKPIs.sentimentMix.positive}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-716">/</span>
            <span className="text-xl text-muted-foreground" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-717">
              {dashboardKPIs.sentimentMix.neutral}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-718">/</span>
            <span className="text-xl text-sentiment-negative" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-719">
              {dashboardKPIs.sentimentMix.negative}
            </span>
          </div>
          <div className="text-sm text-muted-foreground" data-id="element-720">
            Positivi / neutri / negativi
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="bg-card border border-border rounded-lg p-6" data-id="element-721">
        <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-722">
          <div className="flex items-center justify-between mb-3" data-id="element-723">
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-724">
              <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-725" />
              <span data-id="element-726">Riepilogo AI</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-727">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-728">
                <RefreshCw className="h-3 w-3 md:mr-1.5" data-id="element-729" />
                <span className="hidden md:inline" data-id="element-730">Rigenera</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-731">
                <Edit3 className="h-3 w-3 md:mr-1.5" data-id="element-732" />
                <span className="hidden md:inline" data-id="element-733">Modifica</span>
              </Button>
            </div>
          </div>
          <p className="text-base leading-relaxed text-foreground" data-id="element-734">
            {executiveSummary}
          </p>
        </div>
      </section>

      {/* Main content: articles + analytics */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12" data-id="element-735">
        {/* Left: Top articles */}
        <div className="lg:col-span-2" data-id="element-736">
          <div className="flex items-baseline justify-between mb-5" data-id="element-737">
            <h2 className="text-lg font-semibold" data-id="element-738">Top articoli</h2>
            <Button variant="link" size="sm" className="text-xs text-muted-foreground h-auto p-0" data-id="element-739">
              Vedi tutti
            </Button>
          </div>

          <div className="space-y-1" data-id="element-740">
            {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-741">
                {i > 0 && <Separator data-id="element-742" />}
                <button className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-743">
                  <div className="flex items-start gap-4" data-id="element-744">
                    <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-745" />

                    <div className="flex-1 min-w-0" data-id="element-746">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground" data-id="element-747">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 ${getTierColor(article.tier)}`} data-id="element-748">
                          {tierLabels[article.tier]}
                        </Badge>
                        <span data-id="element-749">{article.source}</span>
                        <span data-id="element-750">·</span>
                        <span data-id="element-751">
                          {new Date(article.date).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short'
                      })}
                        </span>
                      </div>

                      <h3 className="font-medium text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-752">
                        {article.title}
                      </h3>

                      <div className="flex items-center gap-5 text-sm" data-id="element-753">
                        <div className="flex items-baseline gap-1.5" data-id="element-754">
                          <span className="font-semibold text-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-755">
                            {article.relevanceScore.toFixed(1)}
                          </span>
                          <span className="text-xs text-muted-foreground" data-id="element-756">
                            score
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-757">
                          <span style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-758">
                            €{(article.ave / 1000).toFixed(1)}k
                          </span>
                          <span className="text-xs" data-id="element-759">AVE</span>
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-760" />
                      </div>
                    </div>
                  </div>
                </button>
              </Fragment>)}
          </div>
        </div>

        {/* Right: unified analytics card */}
        <div className="space-y-8 md:space-y-10" data-id="element-761">
          {/* Sentiment over time */}
          <div data-id="element-762">
            <h3 className="text-sm font-semibold mb-4" data-id="element-763">Sentiment nel tempo</h3>
            <div className="h-40" data-id="element-764">
              <ResponsiveContainer width="100%" height="100%" data-id="element-765">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-766">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-767" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-768" />
                  <YAxis tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-769" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#4A7C59" fill="#4A7C59" fillOpacity={0.5} strokeWidth={1.5} data-id="element-770" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#8B8680" fill="#8B8680" fillOpacity={0.35} strokeWidth={1.5} data-id="element-771" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#B85450" fill="#B85450" fillOpacity={0.5} strokeWidth={1.5} data-id="element-772" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <Separator data-id="element-773" />

          {/* Coverage by Tier */}
          <div data-id="element-774">
            <h3 className="text-sm font-semibold mb-4" data-id="element-775">Copertura per tier</h3>
            <div className="space-y-3" data-id="element-776">
              {coverageByTier.map(item => <div key={item.tier} data-id="element-777">
                  <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-778">
                    <span className="text-foreground" data-id="element-779">{item.tier}</span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-780">
                      {item.count}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden" data-id="element-781">
                    <div className="h-full bg-foreground/80 rounded-full" style={{
                  width: `${item.percentage}%`
                }} data-id="element-782" />
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-783" />

          {/* Top Sources */}
          <div data-id="element-784">
            <h3 className="text-sm font-semibold mb-4" data-id="element-785">Top testate</h3>
            <div className="space-y-2.5" data-id="element-786">
              {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-787">
                  <span className="text-foreground truncate pr-2" data-id="element-788">
                    {source.name}
                  </span>
                  <div className="flex items-center gap-3 text-muted-foreground flex-shrink-0" data-id="element-789">
                    <span style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-790">
                      {source.articles}
                    </span>
                    <span className="text-foreground font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-791">
                      {source.avgScore.toFixed(1)}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-792" />

          {/* Spokespersons */}
          <div data-id="element-793">
            <h3 className="text-sm font-semibold mb-4" data-id="element-794">Portavoce citati</h3>
            <div className="space-y-3" data-id="element-795">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm" data-id="element-796">
                  <div className="min-w-0 pr-2" data-id="element-797">
                    <div className="text-foreground truncate" data-id="element-798">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-799">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-800">
                    <span className="font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-801">
                      {person.mentions}
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-802" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}